<template>
  <Teleport to="body">
    <Transition name="auth-modal-fade">
      <div v-if="authModalStore.isOpen" class="auth-modal-overlay">
        <div
          class="auth-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Sign in or sign up"
        >
          <button
            type="button"
            class="auth-modal-close-btn"
            aria-label="Close authentication modal"
            @click="handleClose"
          >
            <span aria-hidden="true">&times;</span>
          </button>

          <AuthFlowShell :initial-mode="authModalStore.mode" @success="handleSuccess" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AuthFlowShell from '@/components/auth/AuthFlowShell.vue'
import { useAuthModalStore } from '@/stores/authModal'

const authModalStore = useAuthModalStore()
const router = useRouter()
const route = useRoute()

const handleClose = () => {
  authModalStore.close()
}

const handleSuccess = () => {
  authModalStore.close()
}

const onWindowKeydown = () => {
  // Escape key intentionally does not close the modal.
  // Only the close button should dismiss it.
}

// Open modal when URL contains ?signin=true or ?signup=true
watch(
  () => route.query,
  (query) => {
    if (authModalStore.isOpen) return
    if (query.signup === 'true') {
      authModalStore.open('signup')
    } else if (query.signin === 'true') {
      authModalStore.open('signin')
    }
  },
  { immediate: true, deep: true }
)

// Sync URL when modal opens, closes, or mode changes
watch(
  () => ({ isOpen: authModalStore.isOpen, mode: authModalStore.mode }),
  ({ isOpen, mode }) => {
    const newQuery = { ...route.query }
    delete newQuery.signin
    delete newQuery.signup
    if (isOpen) {
      newQuery[mode] = 'true'
    }
    router.replace({ query: newQuery })
  }
)

watch(
  () => authModalStore.isOpen,
  (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
  }
)

onMounted(() => {
  window.addEventListener('keydown', onWindowKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.auth-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

.auth-modal-panel {
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  position: relative;
}

.auth-modal-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 3;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.08);
  color: #333;
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.auth-modal-close-btn:hover {
  background: rgba(17, 24, 39, 0.15);
}

.auth-modal-fade-enter-active,
.auth-modal-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.auth-modal-fade-enter-from,
.auth-modal-fade-leave-to {
  opacity: 0;
}

.auth-modal-fade-enter-from .auth-modal-panel {
  transform: scale(0.95) translateY(10px);
}

.auth-modal-fade-enter-active .auth-modal-panel {
  transition: transform 0.25s ease;
}

@media (max-width: 767px) {
  .auth-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .auth-modal-panel {
    max-width: 100%;
    height: 100dvh;
    overflow-y: auto;
    background: #fff;
    border-radius: 0;
  }

  .auth-modal-close-btn {
    top: 14px;
    right: 14px;
  }
}
</style>
